import classNames from "classnames";
import { useKeyboardListNavigationItem } from "client/Hooks";
import { Key } from "client/Misc/commonTypes";
import { PropsWithChildren, ReactElement, useCallback } from "react";
import styles from "./SuggestionList.module.less";
import { setTrackingId } from "client/Misc/utils";

export type MentionInputSuggestionListProps = {
	trackingId: string;
};

export const MentionInputSuggestionList = (
	props: PropsWithChildren<MentionInputSuggestionListProps>
): ReactElement => {
	const {
		trackingId,
		children
	} = props;

	return (
		<ul
			{...setTrackingId(trackingId)}
			className={styles.root}>
			{children}
		</ul>
	);
};
MentionInputSuggestionList.displayName = "MentionInput.SuggestionList";

type ItemProps = {
	index: number;
	onSelect(index: number): void;
};

const Item = (props: PropsWithChildren<ItemProps>): ReactElement => {
	const {
		index,
		onSelect,
		children
	} = props;

	if (index === 0) {
		return (
			<TopSuggestion>
				{children}
			</TopSuggestion>
		);
	}

	return (
		<SelectableSuggestion
			index={index}
			onSelect={onSelect}
		>
			{children}
		</SelectableSuggestion>
	);
};
Item.displayName = "MentionInput.SuggestionList.Item";
MentionInputSuggestionList.Item = Item;

const TopSuggestion = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<li
			className={classNames(
				styles.item,
				styles.item_top
			)}
		>
			{children}
		</li>
	);
};
TopSuggestion.displayName = "TopSuggestion";

type SelectableSuggestionProps = {
	index: number;
	onSelect(index: number): void;
};

const SelectableSuggestion = (props: PropsWithChildren<SelectableSuggestionProps>): ReactElement => {
	const {
		index,
		onSelect,
		children
	} = props;

	const executeHandler = useCallback(
		(): void => {
			onSelect(index);
		},
		[index, onSelect]
	);

	const keyboardNavigationIndex: number = index - 1;

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: keyboardNavigationIndex,
		onExecute: executeHandler,
		executeKey: [Key.Enter],
		shouldCancelOnTab: true
	});

	return (
		<li
			className={styles.item}
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			{children}
		</li>
	);
};
SelectableSuggestion.displayName = "SelectableSuggestion";